/**
 * FILE-OVERVIEW:
 * - 役割: FullCalendarの描画と操作イベントを処理する主画面。
 * - 主な連携: utils/eventHandlers.js, utils/eventHelpers.js
 * - 本番運用上の注意: eventDrop/eventClickの変更は保存処理と確認ダイアログに影響する。
 */

import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { scheduleEventColors, areaColors } from '../../utils/colors';
import { 
  createDateClickHandler,
  createEventClickHandler,
  createEventDropHandler
} from '../../utils/eventHandlers';
import {
  createViewChangeHandler,
  createEventContentRenderer,
  filterEvents
} from '../../utils/eventHelpers';
import type { CalendarStateApi } from '../../utils/eventHelpers';
import type { CalendarEventsApi } from '../../utils/eventHandlers';
import type { EventDropArg, EventHoveringArg, EventInput } from '@fullcalendar/core';
import type { CalendarEvent } from '../../services/calendarApi';
import type { AuthUser } from '../../hooks/useAuth';

/**
 * 並べ替えの対象。DBのスケジュールと FullCalendar のイベントの両方が渡るため、
 * どちらの持ち方（直下 / extendedProps）でも読めるようにしている
 */
interface SortableEvent {
  id?: string | null;
  start?: string | Date | null;
  end?: string | Date | null;
  category?: string;
  area?: string;
  createdAt?: string;
  created_at?: string;
  extendedProps?: {
    category?: string;
    area?: string;
    createdAt?: string;
    created_at?: string;
    hasStartTime?: boolean;
    hasEndTime?: boolean;
  };
}

interface CalendarMainProps {
  isMobile: boolean;
  calendarState: CalendarStateApi;
  calendarEvents: CalendarEventsApi;
  currentUser: AuthUser | null;
  isReadOnly?: boolean;
}

const SHARED_CALENDAR_VIEW_KEY = 'sharedCalendarView';

const CalendarMain = ({ isMobile, calendarState, calendarEvents, currentUser, isReadOnly = false }: CalendarMainProps) => {
  const previousViewTypeRef = React.useRef<string | null>(null);
  const pendingDropInfoRef = React.useRef<EventDropArg | null>(null);
  const [isDropConfirmOpen, setIsDropConfirmOpen] = React.useState(false);

  const isDateOnlyValue = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const hasTimePart = (value: unknown): boolean => typeof value === 'string' && /T\d{2}:\d{2}/.test(value);

  const toTimestamp = (value: string | Date | null | undefined, fallback = Number.MAX_SAFE_INTEGER): number => {
    if (!value) return fallback;
    const date = value instanceof Date ? value : new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? fallback : time;
  };

  const getCreatedAtValue = (event?: SortableEvent): string => (
    event?.createdAt ||
    event?.created_at ||
    event?.extendedProps?.createdAt ||
    event?.extendedProps?.created_at ||
    ''
  );

  const hasStoredTimePart = (value: unknown): boolean => {
    if (value instanceof Date) return true;
    return hasTimePart(value);
  };

  const detectHasStartTime = (event?: SortableEvent): boolean => {
    if (typeof event?.extendedProps?.hasStartTime === 'boolean') {
      return event.extendedProps.hasStartTime;
    }
    return hasStoredTimePart(event?.start);
  };

  const detectHasEndTime = (event?: SortableEvent): boolean => {
    if (typeof event?.extendedProps?.hasEndTime === 'boolean') {
      return event.extendedProps.hasEndTime;
    }
    return hasStoredTimePart(event?.end);
  };

  const getAreaSortPriority = (event?: SortableEvent): number => {
    const category = event?.category || event?.extendedProps?.category;
    const area = event?.area || event?.extendedProps?.area;

    if (category === 'プライベート') return 3;
    if (area === '本社') return 0;
    if (area === '青森') return 1;
    if (area === '総支局') return 2;
    return 0;
  };

  const compareByStartThenCreated = (a: SortableEvent, b: SortableEvent): number => {
    const aAreaPriority = getAreaSortPriority(a);
    const bAreaPriority = getAreaSortPriority(b);
    if (aAreaPriority !== bAreaPriority) return aAreaPriority - bAreaPriority;

    const aHasStartTime = detectHasStartTime(a);
    const bHasStartTime = detectHasStartTime(b);
    if (aHasStartTime !== bHasStartTime) return aHasStartTime ? -1 : 1;

    const aStart = toTimestamp(a?.start);
    const bStart = toTimestamp(b?.start);
    if (aStart !== bStart) return aStart - bStart;

    const aHasEndTime = detectHasEndTime(a);
    const bHasEndTime = detectHasEndTime(b);
    if (aHasEndTime !== bHasEndTime) return aHasEndTime ? -1 : 1;

    if (!aHasStartTime && aHasEndTime && bHasEndTime) {
      const aEnd = toTimestamp(a?.end);
      const bEnd = toTimestamp(b?.end);
      if (aEnd !== bEnd) return aEnd - bEnd;
    }

    const aCreated = toTimestamp(getCreatedAtValue(a));
    const bCreated = toTimestamp(getCreatedAtValue(b));
    if (aCreated !== bCreated) return aCreated - bCreated;

    const aId = String(a?.id || '');
    const bId = String(b?.id || '');
    return aId.localeCompare(bId, 'ja');
  };

  const mapEventForCalendar = (event: CalendarEvent) => {
    const { startTime, endTime, ...baseEvent } = event;
    const start = event.start;
    const end = event.end || event.start;
    const isAllDay = isDateOnlyValue(start) && isDateOnlyValue(end);
    const hasStartTime = hasTimePart(start);
    const hasEndTime = hasTimePart(end);

    if (!isAllDay) {
      return {
        ...baseEvent,
        hasStartTime,
        hasEndTime,
        allDay: false,
      };
    }

    return {
      ...baseEvent,
      hasStartTime,
      hasEndTime,
      allDay: true,
      end: new Date(new Date(end).getTime() + 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
    };
  };

  const getEventBackgroundColor = (event?: CalendarEvent): string => {
    if (event?.category === 'プライベート') {
      return scheduleEventColors.privateBackground;
    }
    return areaColors[event?.area ?? ''] || scheduleEventColors.background;
  };

  const getEventBorderColor = (event?: CalendarEvent): string => {
    if (event?.category === 'プライベート') {
      return scheduleEventColors.privateBorder;
    }
    if (event?.area === '総支局') {
      return '#0b8043';
    }
    if (event?.area === '青森') {
      return '#4986e7';
    }
    return '#ef6c00';
  };

  const handleDateClick = createDateClickHandler(calendarState);
  const handleEventClick = createEventClickHandler(calendarState, calendarEvents, currentUser);
  const applyEventDrop = React.useMemo(
    () => createEventDropHandler(calendarEvents, currentUser),
    [calendarEvents, currentUser]
  );

  const handleEventDrop = (info: EventDropArg) => {
    if (isReadOnly) {
      info.revert();
      return;
    }

    const droppedEvent = calendarEvents.events.find((event) => String(event.id) === String(info.event.id));
    if (!droppedEvent) {
      info.revert();
      return;
    }

    const isPrivate = String(droppedEvent.category || '').trim() === 'プライベート'
      || droppedEvent.isPrivate === true
      || droppedEvent.isPrivate === 'true'
      || droppedEvent.private === true
      || droppedEvent.private === 'true';
    const ownerUserId = String(droppedEvent.createdBy || '').trim();
    const loginUserId = String(currentUser?.userId || '').trim();
    if (isPrivate && ownerUserId && ownerUserId !== loginUserId) {
      info.revert();
      return;
    }

    pendingDropInfoRef.current = info;
    setIsDropConfirmOpen(true);
  };

  const handleDropConfirmClose = () => {
    pendingDropInfoRef.current?.revert?.();
    pendingDropInfoRef.current = null;
    setIsDropConfirmOpen(false);
  };

  const handleDropConfirmOk = async () => {
    const pendingInfo = pendingDropInfoRef.current;
    pendingDropInfoRef.current = null;
    setIsDropConfirmOpen(false);

    if (!pendingInfo) return;
    await applyEventDrop(pendingInfo);
  };

  const handleEventMouseEnter = (info: EventHoveringArg) => {
    if (!info?.el) return;
    info.el.style.transition = 'opacity 0.15s ease';
    info.el.style.opacity = '0.75';
  };
  const handleEventMouseLeave = (info: EventHoveringArg) => {
    if (!info?.el) return;
    info.el.style.opacity = '1';
  };
  const handleViewChange = createViewChangeHandler(calendarState);
  const renderEventContent = createEventContentRenderer(calendarState.currentView);
  const handleCreateSchedule = () => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    calendarState.setDefaultEvent(dateStr);
    calendarState.setDialogMode?.('schedule');
    calendarState.setOpen(true);
  };

  const handleCreateMemo = () => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    calendarState.setDefaultEvent(dateStr);
    calendarState.setDialogMode?.('memo');
    calendarState.setOpen(true);
  };
  const storedView = sessionStorage.getItem(SHARED_CALENDAR_VIEW_KEY)
    || sessionStorage.getItem('scheduleCalendarView');
  const initialView = isMobile
    ? (storedView === 'dayGridMonth' || storedView === 'dayGridWeek' ? 'listWeek' : (storedView || 'listWeek'))
    : (storedView || 'dayGridMonth');

  // イベントが大量のときに毎レンダーで localeCompare を回さないようメモ化する
  const sortedFilteredEvents = React.useMemo(() => {
    const filtered = filterEvents(
      calendarEvents.events,
      calendarState.selectedCategories,
      calendarState.selectedAreas,
      calendarState.showOnlyMine,
      currentUser?.userId
    );
    return [...filtered].sort(compareByStartThenCreated);
    // compareByStartThenCreated はクロージャ内の純粋関数のため依存配列に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarEvents.events, calendarState.selectedCategories, calendarState.selectedAreas, calendarState.showOnlyMine, currentUser?.userId]);

  return (
    <>
      {/* メインエリア：カレンダー表示 */}
      <Box
        className="calendar-main-container"
        sx={{
          flex: 1,
          p: 2,
          pb: isMobile ? 'calc(var(--mobile-footer-nav-space) + env(safe-area-inset-bottom))' : 2,
          maxWidth: 1680,
          width: '100%',   // 幅を100%に設定
          mx: 'auto',      // 中央寄せ
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        inert={Boolean(calendarState.open)}
      >
        {/* カレンダー本体 */}
        <FullCalendar
          ref={calendarState.calendarRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView={initialView}
          initialDate={sessionStorage.getItem('calendarDate') || undefined}
          // CalendarEvent は id に null を取り得る（複製時の新規扱い）が、
          // 一覧から来るイベントは必ずIDを持つため EventInput として渡す
          events={sortedFilteredEvents.map((event) => ({
            ...mapEventForCalendar(event),
            backgroundColor: getEventBackgroundColor(event),
            borderColor: getEventBorderColor(event),
          })) as EventInput[]}
          // FullCalendar は EventApi を渡してくるが、比較関数は両方の形を読めるようにしてある
          eventOrder={(a: unknown, b: unknown) => compareByStartThenCreated(a as SortableEvent, b as SortableEvent)}
          eventOrderStrict={true}
          eventDisplay="block"
          displayEventTime={false}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          eventContent={renderEventContent}
          locale={jaLocale}
          dateClick={isReadOnly ? undefined : handleDateClick}
          eventClick={handleEventClick}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          eventDrop={isReadOnly ? undefined : handleEventDrop}
          editable={!isReadOnly}
          selectable={!isReadOnly}
          height="100%"
          viewDidMount={handleViewChange}
          customButtons={{
            memoSchedule: {
              text: 'メモなし',
              click: isReadOnly ? () => {} : handleCreateMemo,
            },
            addSchedule: {
              text: '＋',
              click: isReadOnly ? () => {} : handleCreateSchedule,
            },
          }}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: isMobile
              ? (isReadOnly ? '' : 'memoSchedule addSchedule')
              : (isReadOnly ? 'dayGridMonth,dayGridWeek,dayGridDay,listWeek' : 'dayGridMonth,dayGridWeek,dayGridDay,listWeek memoSchedule addSchedule'),
          }}
          datesSet={(info) => {
            const previousViewType = previousViewTypeRef.current;
            const currentViewType = info.view.type;
            const isViewModeChanged = previousViewType && previousViewType !== currentViewType;
            previousViewTypeRef.current = currentViewType;

            if (isViewModeChanged) {
              info.view.calendar.gotoDate(new Date());
            }

            calendarState.setCurrentView(info.view.type);
            sessionStorage.setItem('scheduleCalendarView', info.view.type);
            sessionStorage.setItem(SHARED_CALENDAR_VIEW_KEY, info.view.type);
            sessionStorage.setItem('calendarDate', info.view.currentStart.toISOString());
          }}
          dayHeaderContent={(args) => {
            if (isMobile && calendarState.currentView === 'dayGridWeek') {
              const date = new Date(args.date);
              return date.getDate();
            }
            if (calendarState.currentView.startsWith('list')) {
              const date = new Date(args.date);
              const month = date.getMonth() + 1;
              const day = date.getDate();
              const weekday = date.toLocaleDateString('ja-JP', { weekday: 'narrow' });
              return `${month}月${day}日（${weekday}）`;
            }
            return args.text;
          }}
        />
      </Box>

      <Dialog
        open={isDropConfirmOpen}
        onClose={handleDropConfirmClose}
        aria-labelledby="event-drop-confirm-title"
        aria-describedby="event-drop-confirm-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
          },
        }}
      >
        <DialogTitle id="event-drop-confirm-title">スケジュールを移動しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText id="event-drop-confirm-description">
            このスケジュールの移動を保存します。いいえを押すと元の位置に戻ります。
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDropConfirmClose} variant="outlined" color="inherit">
            いいえ
          </Button>
          <Button onClick={handleDropConfirmOk} variant="contained" autoFocus>
            はい
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarMain;